import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { NO_PERMS, type PermKey, type Perms } from '@farmos/shared';
import { applyLocale, type Locale } from '@/i18n';
import { supabase, unwrap, type Tables } from '@/lib/supabase';

interface AuthState {
  /** undefined while the stored session is being restored */
  session: Session | null | undefined;
  profile: Tables<'profiles'> | null;
  perms: Perms;
  loading: boolean;
  can: (p: PermKey | 'admin') => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === 'SIGNED_OUT') qc.clear();
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  const uid = session?.user.id;
  const me = useQuery({
    queryKey: ['me', uid],
    enabled: !!uid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [profile, perms] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('id', uid!)
          .maybeSingle()
          .then((r) => unwrap(r)),
        supabase.rpc('my_permissions').then((r) => unwrap(r)),
      ]);
      return { profile, perms: { ...NO_PERMS, ...(perms as Partial<Perms>) } };
    },
  });

  useEffect(() => {
    const l = me.data?.profile?.locale as Locale | undefined;
    if (l) applyLocale(l);
  }, [me.data?.profile?.locale]);

  const value = useMemo<AuthState>(() => {
    const perms = me.data?.perms ?? NO_PERMS;
    return {
      session,
      profile: me.data?.profile ?? null,
      perms,
      loading: session === undefined || (!!uid && me.isPending),
      can: (p) => perms[p],
      signOut: async () => {
        await supabase.auth.signOut();
      },
    };
  }, [session, uid, me.data, me.isPending]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
